import { Text, Tooltip } from '@mantine/core';
import { Portal } from '@mantine/core';
import { Group, Menu } from '@mantine/core';
import {
  IconBackspace,
  IconCopy,
  IconDotsVertical,
  IconForms,
  IconLicense,
  IconPencil,
  IconRefresh,
  IconSettings,
  IconTrashX,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import {
  COPY_REVISION_TEMPLATE_PAGE_PATH,
  EDIT_REVISION_TEMPLATE_PAGE_PATH,
  FORMS_PAGE_PATH,
  REVISIONS_PAGE_PATH,
  TEMPLATE_EDITOR_PAGE_PATH,
} from 'routes/paths';
import { RevisionTemplateRow } from 'components/tables/data/revision-template/types';
import RevisionReindexModal from 'components/tables/data/revision-template/RevisionReindexModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useApi } from 'api/api-context';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { CANNOT_SET_ENTITY_STATUS, ERROR_NOTIFICATION_COLOR } from 'utils/constants';
import { notifications } from '@mantine/notifications';
import panic from 'errors/panic';
import { FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR } from 'env';

/**
 * Parameters for the RevisionTemplateActions component.
 */
type RevisionTemplateActionsProps = RevisionTemplateRow & {
  compactOnTablet?: boolean;
  onStatusChange: (status: boolean) => void;
};

/**
 * The actions which can be performed on a single row.
 */
export default function RevisionTemplateActions({
  revisionTemplateId,
  kinds = [],
  slug,
  name,
  status,
  compactOnTablet = false,
  onStatusChange = noop,
}: RevisionTemplateActionsProps) {
  const { getAction, roleId } = useApi();
  const { confirm } = useConfirm();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const [reindexModalOpened, { open: openReindexModal, close: closeReindexModal }] = useDisclosure(false);
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';
  const [canManageBlacklist, setCanManageBlacklist] = useState(false);
  const [canEdit, setCanEdit] = useState(roleId === ROLE_ADMIN_ID);

  const editPath = useMemo(() => EDIT_REVISION_TEMPLATE_PAGE_PATH.insert({ revisionTemplateId }), [revisionTemplateId]);
  const copyPath = useMemo(() => COPY_REVISION_TEMPLATE_PAGE_PATH.insert({ revisionTemplateId }), [revisionTemplateId]);

  const revisionsPath = useMemo(
    () => `${REVISIONS_PAGE_PATH.original}?revisionTemplateSlug=${encodeURIComponent(slug)}`,
    [slug]
  );

  const formsPath = useMemo(
    () => `${FORMS_PAGE_PATH.original}?revisionTemplateSlug=${encodeURIComponent(slug)}`,
    [slug]
  );

  const hasRevisionKind = kinds.includes('revision');
  const hasFormKind = kinds.some((kind) => kind !== 'revision');

  useEffect(() => {
    const revisionTemplateCheckPermissions = getAction('RevisionTemplateCheckPermissions');

    revisionTemplateCheckPermissions({
      parameters: {
        revisionTemplateId: String(revisionTemplateId),
      },
    })
      .then(({ canEdit, canManageBlacklist }) => {
        setCanEdit(canEdit);
        setCanManageBlacklist(canManageBlacklist);
      })
      .catch(panic);
  }, [getAction, revisionTemplateId]);

  /**
   * Makes a call to the API to set the status of the revision template.
   */
  const setStatusRemote = useCallback(
    (status: boolean) => {
      const revisionTemplateSetStatus = getAction('RevisionTemplateSetStatus');

      revisionTemplateSetStatus({ parameters: { revisionTemplateId: String(revisionTemplateId) }, payload: { status } })
        .then(() => onStatusChange(status))
        .catch((error) => {
          if (error.response.data.error.code === CANNOT_SET_ENTITY_STATUS) {
            notifications.show({
              title: 'Chyba!',
              message: 'Túto šablónu nemôžete deaktivovať.',
              color: ERROR_NOTIFICATION_COLOR,
            });
          } else {
            panic(error);
          }
        });
    },
    [getAction, revisionTemplateId]
  );

  /**
   * Confirms the status change.
   */
  const confirmSetStatus = useCallback(
    (status: boolean) => {
      const title = status ? 'Zaradiť šablónu' : 'Vyradiť šablónu';
      const message = status
        ? 'Naozaj chcete zaradiť šablónu?'
        : 'Naozaj chcete vyradiť šablónu? Vyradenie šablóny neovplyvní existujúce revízne správy, ktoré majú priradenú túto šablónu.';

      confirm({
        title,
        content: message,
        onConfirm: () => setStatusRemote(status),
      });
    },
    [confirm, setStatusRemote]
  );

  return (
    <>
      <RevisionReindexModal
        revisionTemplateId={revisionTemplateId}
        slug={slug}
        opened={reindexModalOpened}
        onClose={closeReindexModal}
      />
      <Group spacing={12} noWrap>
        {!menuOnly &&
          (isCompact ? (
            <Tooltip label="Editor šablóny" events={{ hover: true, focus: true, touch: true }}>
              <ActionIcon
                variant="secondary"
                size="md"
                component={Link}
                to={TEMPLATE_EDITOR_PAGE_PATH.insert({ revisionTemplateId })}
                disabled={!status || !canEdit}
              >
                <IconSettings />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Button
              size="md"
              w={172}
              variant="secondary"
              component={Link}
              to={TEMPLATE_EDITOR_PAGE_PATH.insert({ revisionTemplateId })}
              leftIcon={<IconSettings />}
              disabled={!status || !canEdit}
            >
              Editor šablóny
            </Button>
          ))}
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
              <IconDotsVertical stroke="1.5" />
            </ActionIcon>
          </Menu.Target>
          <Portal>
            <Menu.Dropdown>
              <Menu.Label>
                <Text maw={160} truncate>
                  {name}
                </Text>
              </Menu.Label>
              {menuOnly &&
                FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR &&
                (status && canEdit ? (
                  <Menu.Item
                    component={Link}
                    to={TEMPLATE_EDITOR_PAGE_PATH.insert({ revisionTemplateId })}
                    icon={<IconSettings stroke={1.5} size={20} />}
                  >
                    Editor šablóny
                  </Menu.Item>
                ) : (
                  <Menu.Item icon={<IconSettings stroke={1.5} size={20} />} disabled>
                    Editor šablóny
                  </Menu.Item>
                ))}
              {status && (canEdit || canManageBlacklist) && (
                <Menu.Item component={Link} to={editPath} icon={<IconPencil stroke={1.5} size={20} />}>
                  Upraviť parametre
                </Menu.Item>
              )}
              {FEATURE_TOGGLE_REVISION_TEMPLATE_EDITOR && (
                <Menu.Item component={Link} to={copyPath} icon={<IconCopy stroke={1.5} size={20} />}>
                  Kopírovať
                </Menu.Item>
              )}
              {roleId === ROLE_ADMIN_ID && (
                <Menu.Item onClick={openReindexModal} icon={<IconRefresh stroke={1.5} size={20} />}>
                  Reindexovať
                </Menu.Item>
              )}
              <Menu.Divider />
              {hasRevisionKind && (
                <Menu.Item component={Link} to={revisionsPath} icon={<IconLicense stroke={1.5} size={20} />}>
                  Zobraziť revízne správy
                </Menu.Item>
              )}
              {hasFormKind && (
                <Menu.Item component={Link} to={formsPath} icon={<IconForms stroke={1.5} size={20} />}>
                  Zobraziť formuláre
                </Menu.Item>
              )}
              <Menu.Divider />
              {roleId === ROLE_ADMIN_ID && (
                <Menu.Item
                  color={status ? 'red.8' : 'red.8'}
                  onClick={() => confirmSetStatus(!status)}
                  icon={status ? <IconTrashX stroke={1.5} size={20} /> : <IconBackspace stroke={1.5} size={20} />}
                >
                  {status ? 'Vyradiť' : 'Zaradiť'}
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Portal>
        </Menu>
      </Group>
    </>
  );
}
